import type {
  CustomerType,
  RealEstateContact,
  ProcessedListingAddress
} from "../../@types/sharedSchema";
import type { CrmStatusFilterValue } from "./crmStatusFilter";
import type { GridSize } from "@material-ui/core";

export type ContactWithoutId = Pick<
  RealEstateContact,
  | "tags"
  | "roomsMin"
  | "roomsMax"
  | "floorsMin"
  | "floorsMax"
  | "createdTime"
  | "updatedTime"
  | "customerType"
  | "contactOrigin"
  | "customerBudget"
  | "customerBudgetRent"
  | "callerLastName"
  | "callerFirstName"
  | "additionalNotes"
  | "isInSpamDatabase"
  | "callerPhoneNumber"
  | "customerSeriousness"
  | "interestedInLocations"
  | "interestedInPropertyTypes"
  | "optedOutOfBulkMessages"
  | "contactOriginAdditionalInfo"
  | "lastBulkMessageReceivedTime"
>;

export type ActiveColumns = Record<keyof ContactWithoutId, boolean>;

export const initialActiveColumns: ActiveColumns = {
  callerPhoneNumber: true,
  callerFirstName: true,
  callerLastName: true,
  additionalNotes: true,
  createdTime: true,
  updatedTime: false,
  customerType: false,
  customerBudget: false,
  customerBudgetRent: false,
  tags: false,
  interestedInLocations: false,
  interestedInPropertyTypes: false,
  roomsMin: false,
  roomsMax: false,
  floorsMin: false,
  floorsMax: false,
  customerSeriousness: false,
  contactOrigin: false,
  contactOriginAdditionalInfo: false,
  lastBulkMessageReceivedTime: false,
  optedOutOfBulkMessages: false,
  isInSpamDatabase: false
};

export type RangeValue<T> = {
  min: T | null;
  max: T | null;
};

export type DateRangeValue = RangeValue<string>;

export type NumberRangeValue = RangeValue<number>;

export type SortByValue = {
  sortBy: keyof ActiveFilters;
  sortDirection: "asc" | "desc";
};

export type MultipleChoice = Extract<
  keyof RealEstateContact,
  "customerType" | "customerSeriousness" | "customerBudget" | "customerBudgetRent"
>;

type CustomFilter =
  | "tags"
  | "sortBy"
  | "rooms"
  | "hasMeetingOrTask"
  | "interestedInPropertyTypes"
  | "interestedInLocations";

type FilterType = CustomFilter | MultipleChoice | "text" | "date" | "tags" | "radio" | "number";

type FilterValue<T extends FilterType> = T extends "text"
  ? string
  : T extends "number"
  ? number
  : T extends "sortBy"
  ? SortByValue
  : T extends "date"
  ? DateRangeValue
  : T extends "radio"
  ? boolean
  : T extends "rooms" | "customerBudget" | "customerBudgetRent"
  ? NumberRangeValue
  : T extends "customerType"
  ? CustomerType
  : T extends "interestedInLocations"
  ? ProcessedListingAddress[]
  : T extends "interestedInPropertyTypes" | "tags"
  ? string[]
  : // "customerSeriousness" — the historical id of the status filter; its value is now a
    // CRM v2 status (or "hot"), not a legacy seriousness value.
    CrmStatusFilterValue;

export type ActiveFilter<T extends FilterType> = {
  type: T;
  value: null | FilterValue<T>;
  empty?: boolean;
  active: boolean;
};

export type ActiveFilters = {
  sortBy: ActiveFilter<"sortBy">;
  rooms: ActiveFilter<"rooms">;
  hasMeetingOrTask: ActiveFilter<"radio">;
  callerPhoneNumber: ActiveFilter<"text">;
  callerFirstName: ActiveFilter<"text">;
  callerLastName: ActiveFilter<"text">;
  additionalNotes: ActiveFilter<"text">;
  createdTime: ActiveFilter<"date">;
  updatedTime: ActiveFilter<"date">;
  customerType: ActiveFilter<"customerType">;
  customerBudget: ActiveFilter<"customerBudget">;
  customerBudgetRent: ActiveFilter<"customerBudgetRent">;
  tags: ActiveFilter<"tags">;
  interestedInLocations: ActiveFilter<"interestedInLocations">;
  interestedInPropertyTypes: ActiveFilter<"interestedInPropertyTypes">;
  roomsMin: ActiveFilter<"number">;
  roomsMax: ActiveFilter<"number">;
  floorsMin: ActiveFilter<"number">;
  floorsMax: ActiveFilter<"number">;
  customerSeriousness: ActiveFilter<"customerSeriousness">;
  contactOrigin: ActiveFilter<"text">;
  contactOriginAdditionalInfo: ActiveFilter<"text">;
  lastBulkMessageReceivedTime: ActiveFilter<"date">;
  optedOutOfBulkMessages: ActiveFilter<"radio">;
  isInSpamDatabase: ActiveFilter<"radio">;
};

export const initialActiveFilters: ActiveFilters = {
  sortBy: {
    type: "sortBy",
    value: null,
    active: false
  },
  rooms: {
    type: "rooms",
    value: null,
    active: false
  },
  hasMeetingOrTask: {
    type: "radio",
    value: null,
    active: false
  },
  callerPhoneNumber: {
    type: "text",
    value: null,
    empty: false,
    active: false
  },
  callerFirstName: {
    type: "text",
    value: null,
    empty: false,
    active: false
  },
  callerLastName: {
    type: "text",
    value: null,
    empty: false,
    active: false
  },
  additionalNotes: {
    type: "text",
    value: null,
    empty: false,
    active: false
  },
  createdTime: {
    type: "date",
    value: null,
    active: false
  },
  updatedTime: {
    type: "date",
    value: null,
    active: false
  },
  customerType: {
    type: "customerType",
    value: null,
    active: false
  },
  customerBudget: {
    type: "customerBudget",
    value: null,
    empty: false,
    active: false
  },
  customerBudgetRent: {
    type: "customerBudgetRent",
    value: null,
    empty: false,
    active: false
  },
  tags: {
    type: "tags",
    value: null,
    empty: false,
    active: false
  },
  interestedInLocations: {
    type: "interestedInLocations",
    value: null,
    empty: false,
    active: false
  },
  interestedInPropertyTypes: {
    type: "interestedInPropertyTypes",
    value: null,
    empty: false,
    active: false
  },
  roomsMin: {
    type: "number",
    value: null,
    active: false
  },
  roomsMax: {
    type: "number",
    value: null,
    active: false
  },
  floorsMin: {
    type: "number",
    value: null,
    active: false
  },
  floorsMax: {
    type: "number",
    value: null,
    active: false
  },
  customerSeriousness: {
    type: "customerSeriousness",
    value: null,
    active: false
  },
  contactOrigin: {
    type: "text",
    value: null,
    empty: false,
    active: false
  },
  contactOriginAdditionalInfo: {
    type: "text",
    value: null,
    active: false
  },
  lastBulkMessageReceivedTime: {
    type: "date",
    value: null,
    active: false
  },
  optedOutOfBulkMessages: {
    type: "radio",
    value: null,
    active: false
  },
  isInSpamDatabase: {
    type: "radio",
    value: null,
    active: false
  }
};

export type ContactColumnsId = keyof ContactWithoutId | CustomFilter;

export type GridCustomSize = Record<"xs" | "sm" | "md", GridSize>;

export type ContactColumns = {
  id: ContactColumnsId;
  type?: "text" | "textarea" | "number" | "select";
  width?: number;
  index?: number;
  gridCustomSize?: GridCustomSize;
  onlyRealEstate?: boolean;
};

export const initialContactColumns: ContactColumns[] = [
  {
    id: "callerPhoneNumber",
    type: "text",
    index: 0,
    width: 200
  },
  {
    id: "callerFirstName",
    type: "text",
    index: 0,
    width: 150
  },
  {
    id: "callerLastName",
    type: "text",
    width: 150,
    index: 0
  },
  {
    id: "additionalNotes",
    type: "textarea",
    index: 1,
    width: 300
  },
  {
    id: "createdTime",
    width: 200
  },
  {
    id: "updatedTime",
    width: 200
  },
  {
    id: "customerType",
    type: "select",
    index: 0,
    width: 200,
    onlyRealEstate: true
  },
  {
    id: "customerSeriousness",
    type: "select",
    index: 0,
    width: 250,
    onlyRealEstate: true
  },
  {
    id: "customerBudget",
    type: "number",
    index: 0,
    width: 200,
    onlyRealEstate: true
  },
  {
    id: "customerBudgetRent",
    type: "number",
    index: 0,
    width: 200,
    onlyRealEstate: true
  },
  {
    id: "tags",
    width: 200
  },
  {
    id: "interestedInLocations",
    width: 250,
    onlyRealEstate: true
  },
  {
    id: "interestedInPropertyTypes",
    width: 300,
    onlyRealEstate: true
  },
  {
    id: "roomsMin",
    type: "select",
    index: 1,
    width: 150,
    gridCustomSize: { xs: 6, sm: 6, md: 2 },
    onlyRealEstate: true
  },
  {
    id: "roomsMax",
    type: "select",
    index: 1,
    width: 160,
    gridCustomSize: { xs: 6, sm: 6, md: 2 },
    onlyRealEstate: true
  },
  {
    id: "floorsMin",
    type: "select",
    index: 1,
    width: 150,
    gridCustomSize: { xs: 6, sm: 6, md: 2 },
    onlyRealEstate: true
  },
  {
    id: "floorsMax",
    type: "select",
    index: 1,
    width: 160,
    gridCustomSize: { xs: 6, sm: 6, md: 2 },
    onlyRealEstate: true
  },
  {
    id: "contactOrigin",
    width: 200
  },
  {
    id: "contactOriginAdditionalInfo",
    width: 300
  },
  {
    id: "lastBulkMessageReceivedTime",
    width: 300
  },
  {
    id: "optedOutOfBulkMessages",
    type: "select",
    index: 1,
    width: 250,
    gridCustomSize: { xs: 12, sm: 6, md: 4 }
  },
  {
    id: "isInSpamDatabase",
    width: 250
  }
];
